import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Lightbulb } from "lucide-react";

// ── Walkthrough Context ──────────────────────────────────────────────────────
const WalkthroughContext = createContext(null);

export function useWalkthrough() {
  return useContext(WalkthroughContext);
}

export function WalkthroughProvider({ children }) {
  const [steps, setSteps] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [targetRect, setTargetRect] = useState(null);

  const start = useCallback((newSteps) => {
    if (!newSteps?.length) return;
    setSteps(newSteps);
    setActiveIndex(0);
  }, []);

  const next = useCallback(() => {
    setActiveIndex(i => {
      if (i >= steps.length - 1) { setSteps([]); return -1; }
      return i + 1;
    });
  }, [steps.length]);

  const prev = useCallback(() => {
    setActiveIndex(i => Math.max(0, i - 1));
  }, []);

  const close = useCallback(() => {
    setSteps([]);
    setActiveIndex(-1);
    setTargetRect(null);
  }, []);

  const isActive = activeIndex >= 0 && steps.length > 0;
  const currentStep = isActive ? steps[activeIndex] : null;

  return (
    <WalkthroughContext.Provider value={{ start, next, prev, close, isActive, steps, activeIndex, currentStep, targetRect, setTargetRect }}>
      {children}
      {isActive && <SpotlightOverlay />}
    </WalkthroughContext.Provider>
  );
}

// ── Spotlight Overlay ────────────────────────────────────────────────────────
function SpotlightOverlay() {
  const { currentStep, activeIndex, steps, next, prev, close, setTargetRect } = useWalkthrough();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [entering, setEntering] = useState(true);
  const tooltipRef = useRef(null);
  const animFrameRef = useRef(null);

  // Find and track the target element — with route-based navigation
  useEffect(() => {
    if (!currentStep) return;
    setEntering(true);
    const enterTimer = setTimeout(() => setEntering(false), 50);

    // Navigate to the step's route if specified and not already there
    const needsNav = currentStep.route && location.pathname !== currentStep.route;
    if (needsNav) {
      navigate(currentStep.route);
      // Return early — effect re-runs when location.pathname changes
      return () => clearTimeout(enterTimer);
    }

    const findTarget = () => {
      let el = null;

      // Try data-testid first
      if (currentStep.selector) {
        el = document.querySelector(currentStep.selector);
      }
      // Try data-testid shorthand
      if (!el && currentStep.testid) {
        el = document.querySelector(`[data-testid="${currentStep.testid}"]`);
      }
      // Try text content match on sidebar links
      if (!el && currentStep.sidebarPath) {
        el = document.querySelector(`[data-testid="sidebar-link-${currentStep.sidebarPath}"]`);
      }

      if (el) {
        const r = el.getBoundingClientRect();
        const pad = currentStep.padding || 8;
        const newRect = {
          top: r.top - pad,
          left: r.left - pad,
          width: r.width + pad * 2,
          height: r.height + pad * 2,
        };
        setRect(newRect);
        setTargetRect(newRect);

        // Scroll into view if needed
        if (r.top < 0 || r.bottom > window.innerHeight) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        // Fallback: center of screen with a generic highlight
        setRect(null);
        setTargetRect(null);
      }
    };

    // Small delay after navigation to let destination page render
    let observer = null;
    const renderDelay = setTimeout(() => {
      findTarget();
      observer = new ResizeObserver(findTarget);
      observer.observe(document.body);
    }, 300);
    window.addEventListener("scroll", findTarget, true);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(renderDelay);
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", findTarget, true);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [currentStep, setTargetRect, navigate, location.pathname]);

  // Position tooltip relative to the spotlight
  useEffect(() => {
    if (!rect || !tooltipRef.current) return;
    const tt = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 16;

    let top, left;
    const placement = currentStep?.placement || "auto";

    const fits = {
      bottom: rect.top + rect.height + gap + tt.height < vh,
      top: rect.top - gap - tt.height > 0,
      right: rect.left + rect.width + gap + tt.width < vw,
      left: rect.left - gap - tt.width > 0,
    };

    if (placement === "auto") {
      if (fits.bottom) {
        top = rect.top + rect.height + gap;
        left = rect.left + rect.width / 2 - tt.width / 2;
      } else if (fits.top) {
        top = rect.top - gap - tt.height;
        left = rect.left + rect.width / 2 - tt.width / 2;
      } else if (fits.right) {
        top = rect.top + rect.height / 2 - tt.height / 2;
        left = rect.left + rect.width + gap;
      } else {
        top = rect.top - gap - tt.height;
        left = rect.left;
      }
    } else {
      if (placement === "bottom") { top = rect.top + rect.height + gap; left = rect.left + rect.width / 2 - tt.width / 2; }
      else if (placement === "top") { top = rect.top - gap - tt.height; left = rect.left + rect.width / 2 - tt.width / 2; }
      else if (placement === "right") { top = rect.top + rect.height / 2 - tt.height / 2; left = rect.left + rect.width + gap; }
      else { top = rect.top + rect.height / 2 - tt.height / 2; left = rect.left - gap - tt.width; }
    }

    // Clamp within viewport
    left = Math.max(12, Math.min(left, vw - tt.width - 12));
    top = Math.max(12, Math.min(top, vh - tt.height - 12));

    setTooltipPos({ top, left });
  }, [rect, currentStep]);

  if (!currentStep) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9990] transition-opacity duration-200 ${entering ? "opacity-0" : "opacity-100"}`}
      data-testid="walkthrough-overlay"
    >
      {/* Backdrop with spotlight cutout using CSS clip-path */}
      <svg className="fixed inset-0 w-full h-full" style={{ zIndex: 9991 }}>
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%" height="100%"
          fill="rgba(0,0,0,0.72)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Pulse ring around target */}
      {rect && (
        <div
          className="fixed pointer-events-none"
          style={{
            zIndex: 9992,
            top: rect.top - 3,
            left: rect.left - 3,
            width: rect.width + 6,
            height: rect.height + 6,
            borderRadius: 14,
            border: "2px solid rgba(10,132,255,0.5)",
            animation: "spotlight-pulse 2s ease-in-out infinite",
          }}
          data-testid="walkthrough-spotlight-ring"
        />
      )}

      {/* Click guard — clicking backdrop advances or closes */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 9993 }}
        onClick={(e) => {
          // If clicking on the spotlit area, let it through
          if (rect) {
            const { clientX: cx, clientY: cy } = e;
            if (cx >= rect.left && cx <= rect.left + rect.width && cy >= rect.top && cy <= rect.top + rect.height) {
              return;
            }
          }
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed"
        style={{ zIndex: 9994, top: tooltipPos.top, left: tooltipPos.left, maxWidth: 340 }}
        data-testid="walkthrough-tooltip"
      >
        <div className="bg-[var(--app-bg-secondary)] border border-[var(--app-accent)]/30 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--app-border)]">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[var(--app-accent)]/15 flex items-center justify-center">
                <Lightbulb size={12} className="text-[var(--app-accent)]" />
              </div>
              <span className="text-xs font-semibold text-[var(--app-accent)]">
                Step {activeIndex + 1} of {steps.length}
              </span>
            </div>
            <button
              onClick={close}
              className="p-1 rounded-md hover:bg-[var(--app-surface)] text-[var(--app-text-faint)] hover:text-[var(--app-text)]"
              data-testid="walkthrough-close-btn"
            >
              <X size={14} />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3">
            {currentStep.title && (
              <p className="text-sm font-semibold text-[var(--app-text)] mb-1">{currentStep.title}</p>
            )}
            <p className="text-xs text-[var(--app-text-secondary)] leading-relaxed">{currentStep.description}</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-[var(--app-border)]">
            <div className="flex gap-1">
              {steps.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === activeIndex ? "bg-[var(--app-accent)] w-4" : i < activeIndex ? "bg-[var(--app-accent)]/40" : "bg-[var(--app-border)]"
                }`} />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {activeIndex > 0 && (
                <button
                  onClick={prev}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[var(--app-text-muted)] hover:bg-[var(--app-surface)] transition-all"
                  data-testid="walkthrough-prev-btn"
                >
                  <ChevronLeft size={12} /> Back
                </button>
              )}
              <button
                onClick={activeIndex >= steps.length - 1 ? close : next}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-[var(--app-accent)] text-white hover:bg-[var(--app-accent-hover)] transition-all"
                data-testid="walkthrough-next-btn"
              >
                {activeIndex >= steps.length - 1 ? "Done" : "Next"} {activeIndex < steps.length - 1 && <ChevronRight size={12} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spotlight pulse animation */}
      <style>{`
        @keyframes spotlight-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(10,132,255,0.3); }
          50% { box-shadow: 0 0 0 6px rgba(10,132,255,0); }
        }
      `}</style>
    </div>,
    document.body
  );
}
